import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

import { TaskList } from "../tasks.types";
import Divider from "@/components/common/Divider";
import { getImageByPriority } from "@/shared/utils/common";
import { iconMap } from "@/components/common/tasks/utils";
import { cn, formatNumber } from "@/shared/utils";
import StagesCard from "../StageCard";
import IconClose from "@/assets/icons/close.svg?react";
import OptionAwardCard from "../components/cards/OptionAwardCard";

interface PromocodeDrawerProps {
  task: TaskList;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  actionComponent: React.ReactNode;
}

const TaskDrawer = ({
  task,
  onClose,
  actionComponent,
  isOpen,
}: PromocodeDrawerProps) => {
  const closeDrawer = () => {
    onClose();
  };

  const optionAwards = Object.entries(task.option_awards ?? {}).sort(
    ([, a], [, b]) => Number(a) - Number(b)
  );


  const getImgOrIcon = () => {
    if (task.logo?.url) {
      return (
        <img
          src={getImageByPriority(task.logo, "medium")}
          alt="Task Logo"
          className="size-10"
        />
      );
    }

    const Icon = iconMap[task.task_type];
    if (!Icon) {
      return null;
    }
    return <Icon className="size-10 text-white" />;
  };

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && closeDrawer()}>
      <DrawerContent className="bg-gray-4" showTopThumb>
        <DrawerHeader className="flex justify-end items-center">
          <DrawerTitle className="sr-only">{task.name}</DrawerTitle>
          <DrawerClose className="shrink-0">
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>

        <div
          className="overflow-y-auto overflow-x-hidden px-4 pb-safe-bottom"
          style={{ overscrollBehavior: "contain" }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex-center">
              <div className="size-[72px] overflow-hidden rounded-full bg-white/08 flex-center">
                {getImgOrIcon()}
              </div>
            </div>
            <div className="flex flex-col gap-1 items-center">
              <h2 className="text-xl font-bold text-center">{task.name}</h2>
              {/* An option task pays per tier, so a single headline figure is
                  one the user may never be credited — the tier list below is
                  the honest answer, and it is right there. */}
              {optionAwards.length === 0 && (
                <p className="flex items-center gap-0.5 text-orange-100">
                  {formatNumber(task.award)} DON
                </p>
              )}
            </div>
            <Divider className="bg-soft" />
            {task.description && (
              <p
                className="text-xs text-center leading-130 text-white mb-5"
                dangerouslySetInnerHTML={{ __html: task.description }}
              />
            )}
            {optionAwards.length > 0 && (
              // An option task pays a different amount per tier, so the single
              // headline award above does not tell the user what they will get.
              // The tiers are informational: which one was bought comes back
              // from the partner, so there is nothing to pick here.
              // A darker ground than the sheet, so the block reads as a list:
              // on white/08 the white/08 dividers between rows disappeared and
              // the three tiers ran together.
              <div className="rounded-14 border border-white/08 bg-dark/40 overflow-hidden">
                {optionAwards.map(([optionKey, award], index) => (
                  <OptionAwardCard
                    key={optionKey}
                    optionKey={optionKey}
                    award={award}
                    withDivider={index > 0}
                  />
                ))}
              </div>
            )}
            {task.multi_stage ? (
              <div className={cn({ "mt-2": !task.description })}>
                <StagesCard
                  task={task}
                  stages={task.stages}
                  currentAmount={task.user_process?.cur_stage_value}
                  onClose={closeDrawer}
                />
              </div>
            ) : (
              <div className="*:w-full *:text-sm [&_button]:!h-11">
                {actionComponent}
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default TaskDrawer;
